// Cron Health page — read-only view of per-handler cron status.
// Endpoint: GET /api/admin/_cron-health  (closes #181)

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { Button, ErrorChip, PageHeader } from '@awakenedlands/ui';
import { StatusPill, type PillStatus } from '@/components/admin/status-pill';
import { formatAgeSec } from '@/lib/utils/cron-health';

// ── Types ─────────────────────────────────────────────────────────────────────

interface HandlerReport {
  name: string;
  schedule: string;
  lastRunUnix: number | null;
  ageSec: number | null;
  expectedMaxAgeSec: number;
  status: 'ok' | 'stale' | 'missing';
  lastError: string | null;
  lastErrorAt: number | null;
}

interface HealthBody {
  nowUnix: number;
  overall: 'ok' | 'stale' | 'missing';
  handlers: HandlerReport[];
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function CronHealthPage() {
  const [fetchKey, setFetchKey] = useState(0);

  const { data, isLoading, isError, dataUpdatedAt } = useQuery<HealthBody>({
    queryKey: ['cron-health', fetchKey],
    queryFn: async () => {
      const res = await apiFetch<HealthBody>('/admin/_cron-health');
      if (!res.ok) throw new Error('Failed to load cron health.');
      return res.data;
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const lastRefreshedAt = dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString() : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Cron Health"
        description="Per-handler status from the last recorded cron run."
        actions={
          <Button
            type="button"
            variant="secondary"
            onClick={() => setFetchKey((k) => k + 1)}
            disabled={isLoading}
            data-testid="refresh-btn"
          >
            <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            Refresh
          </Button>
        }
      />

      {/* Overall verdict */}
      {data && (
        <div className="flex items-center gap-3 rounded-sm border border-border bg-muted px-4 py-3">
          <span className="text-sm font-medium text-ink-mid">Overall:</span>
          <StatusPill status={data.overall as PillStatus} />
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-ink-low border-t-primary" />
        </div>
      )}

      {/* Error */}
      {isError && (
        <ErrorChip>Failed to load cron health.</ErrorChip>
      )}

      {/* Table */}
      {!isLoading && !isError && data && (
        <>
          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto rounded-sm border border-border">
            <table className="w-full text-sm" data-testid="cron-health-table">
              <thead className="bg-muted text-left">
                <tr>
                  <th className="px-3 py-2 font-medium text-ink-mid">Handler</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Schedule</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Last run</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Age / max</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Status</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Last error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-divider">
                {data.handlers.map((h) => (
                  <tr key={h.name} className="hover:bg-panel-elev" data-testid={`handler-row-${h.name}`}>
                    <td className="px-3 py-2 font-mono text-xs">{h.name}</td>
                    <td className="px-3 py-2 font-mono text-xs text-ink-low">{h.schedule}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-ink-low font-mono tabular-nums text-xs">
                      {h.ageSec !== null ? formatAgeSec(h.ageSec) : '—'}
                    </td>
                    <td className="px-3 py-2 text-ink-low font-mono tabular-nums text-xs">
                      {h.ageSec !== null
                        ? `${formatAgeSec(h.ageSec)} / ${formatAgeSec(h.expectedMaxAgeSec)}`
                        : `— / ${formatAgeSec(h.expectedMaxAgeSec)}`}
                    </td>
                    <td className="px-3 py-2">
                      <StatusPill status={h.status as PillStatus} />
                    </td>
                    <td className="px-3 py-2 text-ink-low max-w-xs truncate text-xs">
                      {h.lastError ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="flex flex-col gap-3 sm:hidden">
            {data.handlers.map((h) => (
              <div
                key={h.name}
                className="rounded-sm border border-border bg-panel p-3"
                data-testid={`handler-card-${h.name}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <span className="font-mono text-xs font-medium text-ink-high break-all">{h.name}</span>
                  <StatusPill status={h.status as PillStatus} />
                </div>
                <div className="space-y-0.5 text-xs text-ink-low font-mono">
                  <div>
                    <span className="text-ink-mid">Schedule </span>
                    {h.schedule}
                  </div>
                  <div className="tabular-nums">
                    <span className="text-ink-mid">Age </span>
                    {h.ageSec !== null
                      ? `${formatAgeSec(h.ageSec)} / ${formatAgeSec(h.expectedMaxAgeSec)}`
                      : `— / ${formatAgeSec(h.expectedMaxAgeSec)}`}
                  </div>
                  {h.lastError && (
                    <div className="text-[--color-status-bad] truncate">{h.lastError}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Last refreshed */}
      {lastRefreshedAt && (
        <p className="text-xs text-ink-low" data-testid="last-refreshed">
          Last refreshed at {lastRefreshedAt}
        </p>
      )}
    </div>
  );
}
